/* Shared engineering-blueprint preview primitives */
import React from "react";

export const BP_THEME = {
  bg: "#0a1128",
  grid: "rgba(120,164,255,0.10)",
  gridMajor: "rgba(120,164,255,0.20)",
  accent: "#fda500",
  dim: "#7da2ff",
  fontMono: "'JetBrains Mono', 'IBM Plex Mono', monospace",
};

export const COLOUR_MAP = {
  "Natural Black": "#1f2330",
  "Grey": "#6b7280",
  "Brown": "#7c5530",
  "Terra Cotta": "#b94f2f",
  "Gold": "#c89a3a",
  "Cream": "#e8d8b0",
};

export function BlueprintDefs({ id, fill }) {
  return (
    <defs>
      <pattern id={`bpgrid-${id}`} width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke={BP_THEME.grid} strokeWidth="0.6" />
      </pattern>
      <pattern id={`bpgrid-major-${id}`} width="100" height="100" patternUnits="userSpaceOnUse">
        <path d="M 100 0 L 0 0 0 100" fill="none" stroke={BP_THEME.gridMajor} strokeWidth="0.8" />
      </pattern>
      <linearGradient id={`fill-${id}`} x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={fill} stopOpacity="1" />
        <stop offset="100%" stopColor={fill} stopOpacity="0.78" />
      </linearGradient>
      <marker id={`arr-l-${id}`} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,4 L8,0 L8,8 z" fill={BP_THEME.accent} />
      </marker>
      <marker id={`arr-r-${id}`} markerWidth="8" markerHeight="8" refX="2" refY="4" orient="auto">
        <path d="M8,4 L0,0 L0,8 z" fill={BP_THEME.accent} />
      </marker>
    </defs>
  );
}

export function BlueprintFrame({ id, w, h, title }) {
  return (
    <g>
      <rect width={w} height={h} fill={`url(#bpgrid-${id})`} />
      <rect width={w} height={h} fill={`url(#bpgrid-major-${id})`} />
      <g stroke={BP_THEME.accent} strokeWidth="0.8" opacity="0.6" fill="none">
        <path d={`M0,16 L0,0 L16,0`} />
        <path d={`M${w-16},0 L${w},0 L${w},16`} />
        <path d={`M0,${h-16} L0,${h} L16,${h}`} />
        <path d={`M${w-16},${h} L${w},${h} L${w},${h-16}`} />
      </g>
      {title && (
        <text x={60} y={22} fontSize="9" fill={BP_THEME.accent} fontFamily={BP_THEME.fontMono} letterSpacing="2">
          {title}
        </text>
      )}
    </g>
  );
}

export function DimH({ id, x1, x2, y, label, color = BP_THEME.accent, fontSize = 10 }) {
  const mid = (x1 + x2) / 2;
  const labelWidth = Math.max(label.length * 6 + 12, 50);
  return (
    <g>
      <line x1={x1} y1={y - 6} x2={x1} y2={y + 6} stroke={BP_THEME.dim} strokeWidth="0.8" />
      <line x1={x2} y1={y - 6} x2={x2} y2={y + 6} stroke={BP_THEME.dim} strokeWidth="0.8" />
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth="1"
            markerStart={`url(#arr-l-${id})`} markerEnd={`url(#arr-r-${id})`} />
      <rect x={mid - labelWidth / 2} y={y - 18} width={labelWidth} height={14} fill={BP_THEME.bg} />
      <text x={mid} y={y - 8} fontSize={fontSize} fill={color}
            fontFamily={BP_THEME.fontMono} textAnchor="middle" fontWeight="700">
        {label}
      </text>
    </g>
  );
}

export function DimV({ id, x, y1, y2, label, color = BP_THEME.accent, fontSize = 10 }) {
  const mid = (y1 + y2) / 2;
  const labelWidth = Math.max(label.length * 6 + 12, 50);
  return (
    <g>
      <line x1={x - 6} y1={y1} x2={x + 6} y2={y1} stroke={BP_THEME.dim} strokeWidth="0.8" />
      <line x1={x - 6} y1={y2} x2={x + 6} y2={y2} stroke={BP_THEME.dim} strokeWidth="0.8" />
      <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth="1"
            markerStart={`url(#arr-l-${id})`} markerEnd={`url(#arr-r-${id})`} />
      <rect x={x - 19} y={mid - labelWidth / 2} width={14} height={labelWidth} fill={BP_THEME.bg} />
      <text x={x - 12} y={mid + 4} fontSize={fontSize} fill={color}
            fontFamily={BP_THEME.fontMono} textAnchor="middle" fontWeight="700"
            transform={`rotate(-90 ${x - 12} ${mid})`}>
        {label}
      </text>
    </g>
  );
}

export function BlueprintFooter({ left, right, y, w, pad = 60 }) {
  return (
    <g>
      <text x={pad} y={y} fontSize="9" fill={BP_THEME.dim} fontFamily={BP_THEME.fontMono}>{left}</text>
      <text x={w - pad / 1.5} y={y} fontSize="9" fill={BP_THEME.accent} fontFamily={BP_THEME.fontMono} textAnchor="end">EAS · DRAWING REV.A</text>
    </g>
  );
}
